import type { ColDef } from "ag-grid-enterprise";
import { mapColDefTree } from "../col_def_tree";

/**
 * Without a comparator AG-Grid compares the cell's date string to a Date with `<`/`>`, which JS
 * coerces lexicographically — always "less than". CSRM-only: SSRM filters server-side.
 */
function parseGridDateString(value: string | null | undefined): Date | null {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/.exec(value) : null;
  if (!match) return null;

  const [, year, month, day, hour, minute, second] = match.map(Number);
  return new Date(year, month - 1, day, hour || 0, minute || 0, second || 0);
}

// Matches AG-Grid's own documented custom date comparator shape — filterLocalDateAtMidnight
// is always a day boundary, so the cell's date is truncated to midnight for comparison.
function dateStringComparator(filterLocalDateAtMidnight: Date, cellValue: string | null | undefined): number {
  const cellDate = parseGridDateString(cellValue);
  if (!cellDate) return -1;

  cellDate.setHours(0, 0, 0, 0);
  if (cellDate < filterLocalDateAtMidnight) return -1;
  if (cellDate > filterLocalDateAtMidnight) return 1;
  return 0;
}

// Descends into ColGroupDef.children for uniformity with the other decorators — inert on
// the vote-by-vote scored grid today, since no column there declares agDateColumnFilter.
export function withDateFilterComparator(columnDefs: ColDef[]): ColDef[] {
  return mapColDefTree(columnDefs, (c) =>
    c.filter === "agDateColumnFilter"
      ? { ...c, filterParams: { ...((c.filterParams as Record<string, unknown>) ?? {}), comparator: dateStringComparator } }
      : c
  );
}
